import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import {
  LuArrowUpRight, LuArrowDownRight, LuMinus, LuMoon, LuScanFace, LuLock,
} from 'react-icons/lu';
import { functions } from '../firebase/Init';
import AppLayout from '../components/oracle/AppLayout';
import PageHeader from '../components/oracle/PageHeader';
import ScoreNumeral from '../components/oracle/ScoreNumeral';
import ScoreTrend from '../components/oracle/ScoreTrend';
import LogoSpinner from '../components/oracle/LogoSpinner';
import EmptyState from '../components/oracle/EmptyState';
import AurumButton from '../components/oracle/AurumButton';
import UnlockProgress from '../components/oracle/UnlockProgress';
import useScanHistory from '../hooks/useScanHistory';
import useIsMobile from '../hooks/useIsMobile';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { computeScoreDelta, daysSince } from '../lib/scoreDelta';
import { formatScore10 } from '../components/create/format';
import { getCategory } from '../contracts/taxonomy';
import { ROUTES, vaultEntryPath } from '../contracts/routes';
import { COPY } from '../contracts/copyMap';
import FeedbackSection from '../components/home/FeedbackSection';

// HOME — the signed-in landing surface.
//
// WHY IT EXISTS
// /create was doing double duty as the home screen. Signing back in dropped you
// on a capture viewport: no score, no movement, no routine, no evidence the
// product remembered you at all. 1.16 sessions per user, 4.2% 30-day return,
// 6.8% scan-to-second-scan. A scanner is a tool you open on purpose, not a
// place to be put.
//
//   ┌──────────────────────────────┐
//   │ HOME                     (◉) │  account moved to the header
//   ├──────────────────────────────┤
//   │  ░ last portrait ░           │
//   │  7.8        LAST SCAN 12 Jun │  gold numeral, as the scan detail shows it
//   │  ▲ +0.4     [ OPEN VERDICT ] │
//   ├──────────────────────────────┤
//   │ YOUR PROGRESS   ┌──────────┐ │  from the FIRST scan, not the second
//   │                 └──────────┘ │
//   ├──────────────────────────────┤
//   │ STRONGEST READINGS           │
//   │  83 Hair  78 Bone  75 Brows  │
//   ├──────────────────────────────┤
//   │ TONIGHT'S ROUTINE — 2 STEPS  │  the affiliate surface, one tap away
//   └──────────────────────────────┘
//
// THE ROUTINE BLOCK IS DELIBERATELY HIGH. Affiliate CTR is a Phase 0 kill gate
// and the routine is the only surface carrying affiliate links. Putting tonight's
// steps on the landing screen is the shortest path from "opened the app" to
// "clicked a product" that does not interrupt anyone.
//
// DEGRADES BY BLOCK, NOT BY PAGE. Scans and routine load from two independent
// callables; either can fail without blanking the other. Neither failure is
// surfaced as an error — this is an orientation screen, and a missing block is
// better than an error page on the surface everyone lands on.

const DELTA_TONE = {
  up: { color: '#8FD2A0', border: 'rgba(143,210,160,0.28)', bg: 'rgba(143,210,160,0.08)', Icon: LuArrowUpRight },
  down: { color: '#D8A0A0', border: 'rgba(216,160,160,0.28)', bg: 'rgba(216,160,160,0.08)', Icon: LuArrowDownRight },
  flat: { color: '#B8B2A6', border: 'rgba(184,178,166,0.24)', bg: 'rgba(184,178,166,0.08)', Icon: LuMinus },
};

const PANEL = 'border border-hairline-soft bg-[#0d0c0b]';

// Readings plotted on the trend. A dozen is a readable series at 375px and
// roughly a quarter's worth of scans at any realistic cadence.
const TREND_POINTS = 12;

function takenLabel(days) {
  if (days == null) return null;
  if (days === 0) return COPY.RETURNING_TODAY;
  if (days === 1) return COPY.RETURNING_YESTERDAY;
  return `${days} ${COPY.RETURNING_DAYS_SUFFIX}`;
}

const DeltaChip = ({ delta }) => {
  const tone = DELTA_TONE[delta.direction] || DELTA_TONE.flat;
  const { Icon } = tone;
  return (
    <span
      data-testid="home-delta"
      data-direction={delta.direction}
      className="inline-flex items-center gap-2 rounded-full px-3 py-1 font-display text-[#E8E2D6]"
      style={{ fontSize: 12, background: tone.bg, border: `1px solid ${tone.border}` }}
    >
      <Icon size={12} style={{ color: tone.color }} />
      {delta.label}
    </span>
  );
};

// ── SEALED READINGS ─────────────────────────────────────────────────────────
//
// listVaultEntries returns `overall_score: null` on a locked row. Every surface
// here has to branch on that BEFORE any arithmetic, because `Number(null)` is
// 0 — not NaN:
//
//   Number(null)              -> 0
//   formatScore10(0)          -> "0.0"      (its "—" branch needs NON-finite)
//   computeScoreDelta(0, 78)  -> "−7.8 since last scan"
//
// So the surface built to reward coming back greeted a first gated scan with
// "you scored 0.0, down 7.8", and the trend chart plotted it at the floor.
// `formatScore10` already renders "—" correctly once it is handed null instead
// of the 0 the coercion produced; this guard is the whole fix.
const isSealed = (entry) => Boolean(entry && entry.locked);

// The score card. The portrait is the backdrop; the score is the same gold
// serif numeral the scan detail uses (ScoreNumeral), so the number means the
// same thing and looks the same wherever it appears.
//
// A DIV, NOT A LINK. The whole card used to be one anchor with the verdict CTA
// as text inside it. Now that the CTA is a real button it needs its own anchor,
// and an anchor inside an anchor is invalid — so the card is inert and the
// button owns the navigation.
export const ScoreHero = ({ latest, previous }) => {
  const sealed = isSealed(latest);
  const score = sealed ? null : Number(latest.overall_score);
  // No delta against a sealed reading in either position: there is no number to
  // compare, and a previous sealed scan would drag the comparison to zero the
  // same way.
  const delta = (!sealed && previous && !isSealed(previous))
    ? computeScoreDelta(score, Number(previous.overall_score))
    : null;
  const days = daysSince(latest.taken_at || latest.timestamp);

  return (
    <div data-testid="home-score-hero" className={`${PANEL} relative overflow-hidden`}>
      {latest.thumbnail_url && (
        <>
          <img
            src={latest.thumbnail_url}
            alt=""
            aria-hidden="true"
            // 30% from the top, not centre: the card is much wider than it is
            // tall, and object-center on a wide box crops a portrait to the
            // middle of the face. Biasing upward keeps the eye-line in frame at
            // every width.
            className="absolute inset-0 h-full w-full object-cover object-[center_30%] opacity-45"
          />
          {/* Reads left-to-right: the face stays legible on the right, the text
              column on the left gets a solid ground. A flat scrim over the whole
              card muddied both. */}
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to right, rgba(8,8,7,0.96) 0%, rgba(8,8,7,0.86) 45%, rgba(8,8,7,0.35) 100%)',
            }}
          />
        </>
      )}

      <div className="relative flex min-h-[172px] flex-col justify-end p-6">
        {/* /10, via the single conversion point (create/format.js). The contract
            scale is 1-100 but every surface that shows the overall — the reveal
            modal, the verdict page, the vault tiles — renders it as 7.8, and
            computeScoreDelta is on that same scale. Showing the raw 78 here put
            "+0.4" next to a two-digit number, which reads as a rounding error
            rather than a four-point move. */}
        {/* A LOCK, not an em dash. "—" is what this surface renders for a
            score that is missing or malformed, so a sealed reading wearing one
            said "there is no number" when the truth is "there is a number and
            it is behind a door you can open". The lock says which.

            Same gold as the numeral it replaces (GOLD_NUMERAL_STYLE is a text
            gradient, so the icon takes `text-aurum` instead) and the same 64px
            box, so the hero does not reflow when a scan unlocks.

            formatScore10 still renders "—" for a genuinely non-finite score —
            but only if it is handed null rather than the 0 that Number(null)
            produces. */}
        {sealed ? (
          <div
            data-testid="home-score-locked"
            role="img"
            aria-label={COPY.VAULT_TILE_LOCKED_A11Y}
            className="flex h-16 w-16 items-center justify-center"
          >
            <LuLock size={44} className="text-aurum" aria-hidden="true" />
          </div>
        ) : (
          <ScoreNumeral value={formatScore10(score)} size={64} />
        )}

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <span
            className="font-display uppercase tracking-[0.24em] text-bone-dim"
            style={{ fontSize: 10 }}
          >
            {COPY.HOME_LAST_SCAN_LABEL}
            {takenLabel(days) ? ` — ${takenLabel(days)}` : ''}
          </span>
          {delta && <DeltaChip delta={delta} />}
          {sealed && (
            <span
              data-testid="home-sealed-badge"
              className="font-display uppercase tracking-[0.24em] text-aurum"
              style={{ fontSize: 10 }}
            >
              {COPY.VAULT_TILE_LOCKED}
            </span>
          )}
        </div>

        {/* How close they are to opening it — the same ten cells the unlock
            modal and Explore draw. Without this the hero says only that the
            number is withheld, not that it is EARNABLE, which is the one thing
            this surface exists to communicate. */}
        {sealed && latest.unlock && (
          <UnlockProgress
            done={latest.unlock.done}
            required={latest.unlock.required}
            className="mt-4"
            label={COPY.UNLOCK_GATE_EARN_COUNT_LABEL}
          />
        )}

        {/* Same treatment as OPEN ROUTINE below — both are "go read the thing"
            actions and were reading as different tiers of control.

            A sealed reading points at Explore instead: the verdict page would
            only show the same gate again, one navigation later. */}
        <Link
          to={sealed ? `${ROUTES.JUDGE}?unlock=${latest.scan_id}` : vaultEntryPath(latest.scan_id)}
          className="mt-5 block no-underline"
        >
          <AurumButton fullWidth data-testid="home-verdict-cta">
            {sealed ? COPY.HOME_SEALED_CTA : COPY.HOME_VERDICT_CTA}
          </AurumButton>
        </Link>
      </div>
    </div>
  );
};

// Score over recent readings. Rendered from the FIRST scan onward: the axes and
// grid place that reading on the scale, which the hero's bare number does not,
// and ScoreTrend says plainly that a second scan is what turns it into a trend.
// Hiding it until scan two put an empty space on the surface built to reward
// coming back, shown to exactly the people one scan away from doing so.
//
// `entries` arrives newest-first from listVaultEntries; a chart reads
// left-to-right through time, so it is reversed here rather than inside
// ScoreTrend, which takes plain ascending data.
export const TrendSection = ({ entries }) => {
  // Recharts sizes its width from the container but takes height as a number,
  // so the breakpoint has to be read at runtime rather than expressed in a
  // `lg:` class. A phone-height chart stretched across the desktop content
  // column reads as a flat line regardless of the data.
  const isMobile = useIsMobile();
  // Sealed readings carry `overall_score: null` and are DROPPED, not plotted.
  // Plotting them puts a point at zero — Number(null) is 0 — which reads as a
  // catastrophic drop rather than a withheld number, and drags the whole axis
  // down with it. A gap in the series is the honest rendering of a score that
  // exists but is not being shown yet.
  const chronological = (entries || []).filter((e) => !isSealed(e)).slice().reverse();
  if (chronological.length === 0) return null;

  return (
    <section data-testid="home-trend" className="mt-8">
      <h2
        className="m-0 font-display uppercase tracking-[0.24em] text-[#E8E2D6]"
        style={{ fontSize: 11 }}
      >
        {COPY.HOME_TREND_EYEBROW}
      </h2>
      <div className={`${PANEL} mt-4 px-3 pt-4 pb-2`}>
        <ScoreTrend entries={chronological} height={isMobile ? 120 : 190} />
      </div>
    </section>
  );
};

// Absent on legacy pre-taxonomy scans — listVaultEntries returns [] for those,
// and this renders nothing rather than three empty boxes.
export const CategoryTiles = ({ categories }) => {
  if (!categories || categories.length === 0) return null;
  return (
    <section data-testid="home-categories" className="mt-8">
      <h2
        className="m-0 font-display uppercase tracking-[0.24em] text-[#E8E2D6]"
        style={{ fontSize: 11 }}
      >
        {COPY.HOME_CATEGORIES_EYEBROW}
      </h2>
      <ul
        className="mt-4 grid list-none gap-3 p-0 m-0"
        style={{ gridTemplateColumns: `repeat(${categories.length}, minmax(0, 1fr))` }}
      >
        {categories.map((c) => {
          const meta = getCategory(c.key);
          return (
            <li key={c.key} className={`${PANEL} px-3 py-4`}>
              {/* Aurum on a metric value — the sanctioned use (DESIGN.md §2.1). */}
              <div
                className="font-display text-aurum leading-none tabular-nums"
                style={{ fontSize: 28 }}
              >
                {c.score}
              </div>
              <div
                className="mt-2 font-display uppercase tracking-[0.14em] text-bone-dim"
                style={{ fontSize: 9.5, lineHeight: 1.4 }}
              >
                {meta ? meta.label : c.key}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

// Evening steps only. AM and PM together would be the routine page; the point
// here is one glanceable "what do I do tonight", which is the adherence
// question that actually decides whether anyone comes back.
export const TonightRoutine = ({ pm }) => {
  if (!pm) return null;
  const steps = pm.slice(0, 3);
  return (
    <section data-testid="home-routine" className="mt-8">
      <div className="flex items-baseline justify-between gap-4">
        <h2
          className="m-0 flex items-center gap-2 font-display uppercase tracking-[0.24em] text-[#E8E2D6]"
          style={{ fontSize: 11 }}
        >
          <LuMoon size={13} aria-hidden="true" />
          {COPY.HOME_ROUTINE_EYEBROW}
        </h2>
        {pm.length > 0 && (
          <span
            className="font-display uppercase tracking-[0.2em] text-bone-dim"
            style={{ fontSize: 10 }}
          >
            {pm.length}{' '}
            {pm.length === 1 ? COPY.HOME_ROUTINE_STEP_SUFFIX : COPY.HOME_ROUTINE_STEPS_SUFFIX}
          </span>
        )}
      </div>

      <div className={`${PANEL} mt-4`}>
        {pm.length === 0 ? (
          <p
            className="m-0 px-5 py-6 font-display italic text-bone-dim"
            style={{ fontSize: 14, lineHeight: 1.5 }}
          >
            {COPY.HOME_ROUTINE_EMPTY}
          </p>
        ) : (
          <ul className="m-0 list-none p-0">
            {steps.map((a) => (
              <li
                key={a.active_id}
                className="flex items-center justify-between gap-4 border-b border-hairline-soft px-5 py-4 last:border-b-0"
              >
                <span className="min-w-0">
                  <span
                    className="block truncate font-display text-[#E8E2D6]"
                    style={{ fontSize: 15 }}
                  >
                    {a.active}
                  </span>
                  <span
                    className="mt-1 block truncate font-display text-bone-dim"
                    style={{ fontSize: 12 }}
                  >
                    {a.treats}
                  </span>
                </span>
                <span
                  className="shrink-0 font-display uppercase tracking-[0.18em] text-bone-dim"
                  style={{ fontSize: 9.5 }}
                >
                  {a.step}
                </span>
              </li>
            ))}
          </ul>
        )}

        <div className="border-t border-hairline-soft p-4">
          <Link to={ROUTES.ROUTINE} className="block no-underline">
            <AurumButton fullWidth data-testid="home-routine-cta">
              {COPY.HOME_ROUTINE_CTA}
            </AurumButton>
          </Link>
        </div>
      </div>
    </section>
  );
};

// PM actives, or null while loading / on failure. Never throws upward — a dead
// routine call must not take the landing screen down with it.
function useTonightRoutine() {
  const [pm, setPm] = useState(null);

  const load = useCallback(async (alive) => {
    try {
      const getRoutine = httpsCallable(functions, 'getRoutine');
      const res = await getRoutine({});
      const data = res && res.data;
      if (!alive()) return;
      if (data && data.status === 'success' && Array.isArray(data.pm)) setPm(data.pm);
    } catch (err) {
      console.error('Home: routine load failed', err);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    load(() => mounted);
    return () => {
      mounted = false;
    };
  }, [load]);

  return pm;
}

const Home = () => {
  const navigate = useNavigate();
  useDocumentTitle(COPY.HOME_TITLE);

  // More than the default 2: the trend chart plots every point it receives.
  // listVaultEntries signs a Storage URL per entry, so this is a real cost —
  // capped at a dozen, which is a readable series on a 375px screen and roughly
  // a quarter's worth of readings at any realistic cadence.
  const { phase, entries, latest, previous, hasScans } = useScanHistory({ limit: TREND_POINTS });
  const pm = useTonightRoutine();

  return (
    <AppLayout header={<PageHeader title={COPY.HOME_TITLE} subtitle={COPY.HOME_SUBTITLE} />}>
      <div className="px-5 lg:px-12 pb-10 lg:pb-14">
        {(phase === 'idle' || phase === 'loading') && (
          <div className="flex justify-center py-16">
            <LogoSpinner />
          </div>
        )}

        {/* `error` lands here too, not on an error state: a failed history read
            is indistinguishable to the user from having no scans, and the
            correct next action ("scan") is the same either way. */}
        {phase !== 'idle' && phase !== 'loading' && !hasScans && (
          <EmptyState
            icon={<LuScanFace size={22} />}
            headline={COPY.HOME_EMPTY_TITLE}
            subtitle={COPY.HOME_EMPTY_BODY}
            ctaLabel={COPY.HOME_EMPTY_CTA}
            onCta={() => navigate(ROUTES.CREATE)}
          />
        )}

        {phase === 'ready' && hasScans && (
          <>
            <ScoreHero latest={latest} previous={previous} />
            <TrendSection entries={entries} />
            {/* Peer readings REPLACE the model's category tiles rather than
                stacking under them. Same information (a category readout),
                sourced from people instead of the model — and keeping the page
                length flat matters, because TonightRoutine is deliberately high
                for affiliate CTR and must not get pushed down. Tiles remain the
                fallback until someone has actually read the scan. */}
            {latest.peer_feedback
              ? <FeedbackSection scanId={latest.scan_id} feedback={latest.peer_feedback} />
              : <CategoryTiles categories={latest.top_categories} />}
            <TonightRoutine pm={pm} />

            {/* The page's primary action carries the heaviest treatment. 44% of
                registered accounts have never scanned once; the routine CTA
                above earns its prominence from position, not button weight. */}
            <Link to={ROUTES.CREATE} className="mt-8 block no-underline">
              <AurumButton variant="metallic" fullWidth data-testid="home-rescan-cta">
                {COPY.HOME_RESCAN_CTA}
              </AurumButton>
            </Link>
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default Home;
